import { createStyles, Theme } from "@material-ui/core";

export const externalCalendarStyles = (theme: Theme) => createStyles({
  paper: {
    borderRadius: 20,
    maxHeight: "calc(100% - 48px)",
    [theme.breakpoints.down("xs")]: { margin: 12, width: "calc(100% - 24px)", maxHeight: "calc(100% - 24px)" },
  },
  header: { display: "flex", alignItems: "flex-start", padding: "24px 24px 12px", gap: 12 },
  heading: { flex: 1, minWidth: 0 },
  title: { margin: 0, fontSize: 23, fontWeight: 600, lineHeight: 1.3, overflowWrap: "break-word" },
  description: { margin: "10px 0 0", color: theme.palette.text.secondary, fontSize: 14, lineHeight: 1.6 },
  content: { padding: "8px 24px 24px", overflowY: "auto", [theme.breakpoints.down("xs")]: { padding: "8px 18px 20px" } },
  info: {
    display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 14px", borderRadius: 12,
    background: theme.palette.action.hover, color: theme.palette.text.secondary, fontSize: 13, lineHeight: 1.6,
    "& svg": { flexShrink: 0, marginTop: 2, fontSize: 18 },
  },
  list: { display: "flex", flexDirection: "column", gap: 14, marginTop: 18 },
  card: { border: `1px solid ${theme.palette.divider}`, borderRadius: 14, padding: 18, background: theme.palette.background.paper },
  cardHeader: { display: "flex", gap: 12, alignItems: "center" },
  calendarIcon: {
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
    width: 44, height: 44, borderRadius: 12, background: theme.palette.action.hover,
    color: theme.palette.type === "dark" ? theme.palette.primary.light : theme.palette.primary.main,
  },
  cardTitle: { fontSize: 18, fontWeight: 600, margin: 0, lineHeight: 1.4, overflowWrap: "break-word" },
  source: { margin: "4px 0 0", color: theme.palette.text.secondary, fontSize: 12, lineHeight: 1.5, overflowWrap: "anywhere" },
  metadata: { margin: "16px 0 0", display: "grid", gridTemplateColumns: "auto minmax(0, 1fr)", gap: "12px 16px", fontSize: 13, lineHeight: 1.5 },
  metaLabel: { color: theme.palette.text.secondary, paddingTop: 3 },
  metaValue: { margin: 0, display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px 10px", minWidth: 0, overflowWrap: "anywhere" },
  person: { display: "inline-flex", alignItems: "center", gap: 7, minWidth: 0 },
  cardFooter: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, marginTop: 16, paddingTop: 12, borderTop: `1px solid ${theme.palette.divider}` },
  ownership: { flex: 1, color: theme.palette.text.secondary, fontSize: 12, lineHeight: 1.5 },
  actionButton: {
    "&&": { minHeight: 42, fontSize: 14, fontWeight: 600, lineHeight: 1.4, letterSpacing: 0, textTransform: "none", borderRadius: 24, padding: "10px 18px" },
  },
  textButton: {
    "&&": { minHeight: 40, fontSize: 13, fontWeight: 600, lineHeight: 1.4, letterSpacing: 0, textTransform: "none", padding: "8px 12px" },
  },
  footer: {
    display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0,
    gap: 12, padding: "16px 24px", borderTop: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("xs")]: { padding: "14px 18px", flexWrap: "wrap", "& > :last-child": { flexGrow: 1 } },
  },
  overviewFooter: { justifyContent: "flex-end" },
  empty: { textAlign: "center", padding: "32px 12px 20px", "& > svg": { fontSize: 48, color: theme.palette.primary.light, marginBottom: 16 } },
  emptyTitle: { margin: 0, fontSize: 20, fontWeight: 600, lineHeight: 1.4 },
  steps: { display: "flex", gap: 16, padding: 0, margin: "0 0 24px", listStyle: "none" },
  step: { display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: theme.palette.text.secondary, lineHeight: 1.4 },
  activeStep: { color: theme.palette.text.primary, fontWeight: 600 },
  stepNumber: {
    width: 25, height: 25, flexShrink: 0, display: "flex", alignItems: "center",
    justifyContent: "center", borderRadius: "50%", background: theme.palette.action.hover,
  },
  activeStepNumber: { background: theme.palette.primary.main, color: theme.palette.primary.contrastText },
  sectionTitle: { margin: "0 0 6px", fontSize: 18, lineHeight: 1.4, fontWeight: 600 },
  field: { display: "flex", flexDirection: "column", gap: 8, marginTop: 22 },
  label: { fontSize: 14, fontWeight: 600, lineHeight: 1.5 },
  input: {
    boxSizing: "border-box", width: "100%", minHeight: 48, padding: "12px 14px", borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`, background: theme.palette.background.paper,
    color: theme.palette.text.primary, font: "inherit", fontSize: 16,
    "&:focus": { outline: `2px solid ${theme.palette.primary.light}`, outlineOffset: 1 },
    "&[aria-invalid=true]": { borderColor: theme.palette.error.main },
  },
  hint: { margin: 0, color: theme.palette.text.secondary, fontSize: 13, lineHeight: 1.6 },
  help: {
    marginTop: 22, padding: "14px 16px", borderRadius: 12, background: theme.palette.action.hover,
    fontSize: 13, lineHeight: 1.6, color: theme.palette.text.secondary,
    "& summary": { cursor: "pointer", color: theme.palette.text.primary, fontWeight: 600 },
    "& p": { margin: "14px 0 0" },
    "& strong": { display: "block", color: theme.palette.text.primary },
  },
  success: { display: "flex", gap: 10, alignItems: "flex-start", padding: 14, marginBottom: 20, borderRadius: 12, border: `1px solid ${theme.palette.divider}`, fontSize: 13, lineHeight: 1.6, "& svg": { color: theme.palette.success.main, flexShrink: 0 } },
  error: { color: theme.palette.error.main, fontSize: 13, lineHeight: 1.6, margin: "12px 0 0" },
  sharing: { border: 0, padding: 0, margin: "24px 0 0", minWidth: 0 },
  sharingOptions: { display: "flex", flexDirection: "column", gap: 4, marginTop: 10 },
  sharingOption: { margin: 0, padding: "3px 4px", borderRadius: 10, "&:hover": { background: theme.palette.action.hover } },
  sharingName: { display: "flex", alignItems: "center", gap: 10, fontSize: 14, lineHeight: 1.5, overflowWrap: "anywhere" },
  danger: { "&&": { color: theme.palette.error.main } },
  dangerArea: { borderTop: `1px solid ${theme.palette.divider}`, marginTop: 24, paddingTop: 14 },
  confirmation: { border: `1px solid ${theme.palette.divider}`, borderRadius: 12, padding: 16, marginTop: 10 },
  confirmationActions: { display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 },
  // Also used by the existing export dialog.
  importedActions: { display: "flex", justifyContent: "space-between", padding: "24px", gap: "12px" },
});



// WEBPACK FOOTER //
// ./src/components/calendar2/externalCalendar/externalCalender.style.ts